import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Plus, Save, Check, Edit2, CircleMinus, CircleCheck, Lock } from "lucide-react";
import FormControl from "../../common/FormControl";
import Modal from "../../common/Modal";
import Button from "../../common/Button";
import Skeleton from "../../common/Skeleton";
import Pagination from "../../common/Pagination";
import NoDataFound from "../../common/NoDataFound";
import Toast, { type ToastMessage } from "../../common/Toast";
import StatusSwitch from "../../common/StatusSwitch";
import ApprovalDot from "../../common/ApprovalDot";
import WorkflowModal from "../../common/WorkflowModal";
import { confirmWarning, confirmActive } from "../../common/confirm";
import { usePermission, NOT_AUTHORISED_MSG } from "../../../contexts/PermissionContext";
import {
    listBalances, createBalance, updateBalance, listLeaveTypes, setBalanceActive,
    type HrStatus,
} from "../../../services/hrService";
import { userService } from "../../../services/userService";
import { LeaveBalance, LeaveType } from "../../../services/hrTypes";

const RBAC_ACTIVITY = "HR_LEAVE_BALANCES";

interface UserOpt { id: number; name: string; email: string; }

const LeaveBalancesAdmin: React.FC = () => {
    // Leave Balances is a maker-checker master gated on HR_LEAVE_BALANCES.
    const viewPerm = usePermission(RBAC_ACTIVITY, "view");
    const modifyPerm = usePermission(RBAC_ACTIVITY, "view+modify");
    const canView = viewPerm.allowed;
    const canModify = modifyPerm.allowed;
    const permLoading = viewPerm.loading;

    const [balances, setBalances] = useState<LeaveBalance[]>([]);
    const [types, setTypes] = useState<LeaveType[]>([]);
    const [users, setUsers] = useState<UserOpt[]>([]);
    const [statusFilter, setStatusFilter] = useState<HrStatus>("active");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState<ToastMessage | null>(null);

    const [editing, setEditing] = useState<LeaveBalance | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [isFieldEditMode, setIsFieldEditMode] = useState(false);
    const [staffUserId, setStaffUserId] = useState(0);
    const [leaveTypeId, setLeaveTypeId] = useState(0);
    const [year, setYear] = useState(new Date().getFullYear());
    const [allotted, setAllotted] = useState(0);
    const [saving, setSaving] = useState(false);
    const [isActive, setIsActive] = useState(true);
    const [togglingActive, setTogglingActive] = useState(false);
    const [workflowOpenFor, setWorkflowOpenFor] = useState<LeaveBalance | null>(null);

    const fetchAll = useCallback(async () => {
        if (!canView) { setLoading(false); return; }
        setLoading(true);
        try {
            const [b, t, u] = await Promise.all([listBalances(statusFilter), listLeaveTypes(), userService.getUsersDropdown()]);
            setBalances(b);
            setTypes(t);
            setUsers(u);
        } catch (err: any) {
            setToast({ type: "error", message: err.response?.data?.detail || err.message || "Failed to load" });
        } finally {
            setLoading(false);
        }
    }, [canView, statusFilter]);

    useEffect(() => { fetchAll(); }, [fetchAll]);
    useEffect(() => { setPage(1); }, [statusFilter, pageSize]);

    const paginatedBalances = useMemo(
        () => balances.slice((page - 1) * pageSize, page * pageSize),
        [balances, page, pageSize]
    );

    const typeName = (id: number) => types.find((t) => t.id === id)?.name || `Type #${id}`;
    const userName = (id: number) => users.find((u) => u.id === id)?.name || `User #${id}`;

    const openCreate = () => {
        setEditing(null); setStaffUserId(0); setLeaveTypeId(0);
        setYear(new Date().getFullYear()); setAllotted(0);
        setIsFieldEditMode(true);   // create form is editable from the start
        setIsOpen(true);
    };
    const openEdit = (b: LeaveBalance) => {
        setEditing(b); setStaffUserId(b.staff_user_id); setLeaveTypeId(b.leave_type_id);
        setYear(b.year); setAllotted(b.allotted); setIsActive(b.is_active !== 0);
        setIsFieldEditMode(false);  // open read-only — "Edit" unlocks the fields
        setIsOpen(true);
    };

    const isApproved = editing?.workflow_state === "approved";
    // Edit form is read-only until the user clicks Edit; approved rows stay locked.
    const fieldsDisabled = !!editing && (!canModify || isApproved || !isFieldEditMode);

    const handleSave = async () => {
        if (!editing && (!staffUserId || !leaveTypeId)) { setToast({ type: "error", message: "Select a staff member and leave type." }); return; }
        setSaving(true);
        try {
            if (editing) {
                await updateBalance(editing.id, { allotted: Number(allotted) || 0 });
            } else {
                await createBalance({
                    staff_user_id: staffUserId,
                    leave_type_id: leaveTypeId,
                    year: Number(year),
                    allotted: Number(allotted) || 0,
                    used: 0,
                    pending: 0,
                } as any);
            }
            setToast({ type: "success", message: editing ? "Balance updated" : "Balance assigned" });
            setIsOpen(false);
            fetchAll();
        } catch (err: any) {
            setToast({ type: "error", message: err.response?.data?.detail || err.message || "Save failed" });
        } finally {
            setSaving(false);
        }
    };

    // Active/Inactive lifecycle toggle (is_active 1↔0) — fires immediately from the
    // edit modal, independent of field edits. Mirrors LeaveRules for consistency.
    const handleToggleActive = async (next: boolean) => {
        if (!editing) return;
        const confirmFn = next ? confirmActive : confirmWarning;
        const ok = await confirmFn({
            title: next ? "Reactivate this balance?" : "Inactivate this balance?",
            text: next ? "It will appear in active lists again." : "It will be hidden from active lists.",
            confirmText: next ? "Yes, Reactivate" : "Yes, Inactivate",
        });
        if (!ok) return;
        setTogglingActive(true);
        try {
            const updated = await setBalanceActive(editing.id, next);
            setIsActive(next);
            setEditing(updated);
            setToast({ type: next ? "success" : "warning", message: next ? "Balance reactivated." : "Balance inactivated." });
            fetchAll();
        } catch (err: any) {
            setToast({ type: "error", message: err.response?.data?.detail || err.message || "Failed to update status." });
        } finally {
            setTogglingActive(false);
        }
    };

    // Workflow (submit/approve/reject/reopen) is driven end-to-end by the unified
    // <WorkflowModal> via the /api/v1/wf engine (kind="hr_leave_balance").
    const closeWf = () => setWorkflowOpenFor(null);

    // ── Not-authorised gate ────────────────────────────────────────────────────
    if (!permLoading && !canView) {
        return (
            <div className="netbody-card flex flex-col h-full">
                <NoDataFound title="Not authorised" message={viewPerm.message || NOT_AUTHORISED_MSG} />
            </div>
        );
    }

    const headerText = editing
        ? isApproved ? <>Viewing (locked) "<span className="item-highlight">{editing.staff_user_id ? userName(editing.staff_user_id) : "Balance"}</span>"</>
        : (canModify && isFieldEditMode) ? <>Editing "<span className="item-highlight">{editing.staff_user_id ? userName(editing.staff_user_id) : "Balance"}</span>"</>
        : <>Viewing "<span className="item-highlight">{editing.staff_user_id ? userName(editing.staff_user_id) : "Balance"}</span>"</>
        : "Assign Balance";

    const isInvalid = !editing && (!staffUserId || !leaveTypeId);
    const showCreateBtn = !editing && canModify;
    const showEditActions = !!editing && canModify && !isApproved;

    return (
        <div className="netbody-card flex flex-col h-full">
            <div className="netbody-header">
                <StatusSwitch
                    value={statusFilter === "inactive" ? "INACTIVE" : "ACTIVE"}
                    onChange={(next) => setStatusFilter(next === "INACTIVE" ? "inactive" : "active")}
                    noun="balances"
                    testId="SEL_balance_status"
                />
                <div className="ml-auto">
                    {canModify && <Button variant="fill" icon={Plus} onClick={openCreate} data-testid="BTN_balance_create">Assign</Button>}
                </div>
            </div>

            <div className="netbody-content">
                <div className="scroll-container flex-1 overflow-hidden">
                <div className="table-list">
                    <div className="table-list-scroll">
                    <table className="table-list-table">
                        <thead>
                            <tr>
                                <th className="table-list-th" style={{ width: 34 }} aria-label="Workflow status" />
                                <th className="table-list-th table-list-cell--sno">S.No</th>
                                <th className="table-list-th">Code</th>
                                <th className="table-list-th">Staff</th>
                                <th className="table-list-th">Leave Type</th>
                                <th className="table-list-th">Year</th>
                                <th className="table-list-th">Allotted</th>
                                <th className="table-list-th">Used</th>
                                <th className="table-list-th">Pending</th>
                                <th className="table-list-th">Status</th>
                            </tr>
                        </thead>
                        <tbody aria-busy={loading} aria-live="polite">
                            {loading && <Skeleton.TableRows cols={["pill", "3xs", "2xs", "md", "sm", "2xs", "2xs", "2xs", "2xs", "pill"]} />}
                            {!loading && balances.length === 0 && (
                                <tr>
                                    <td colSpan={10} className="table-list-cell">
                                        <NoDataFound title="No balances" message="Assign a leave balance to a staff member." />
                                    </td>
                                </tr>
                            )}
                            {!loading && paginatedBalances.map((b, idx) => (
                                <tr
                                    key={b.id}
                                    className="table-list-row"
                                    data-testid={`ROW_balance_${b.id}`}
                                    onClick={() => openEdit(b)}
                                    style={{ cursor: "pointer" }}
                                >
                                    <td className="table-list-cell" style={{ width: 34 }} onClick={(e) => { e.stopPropagation(); setWorkflowOpenFor(b); }} title="Workflow & history" data-testid={`DOT_balance_wf_${b.id}`}>
                                        <ApprovalDot
                                            state={(b.workflow_state || "draft") as any}
                                            onClick={(e) => { e.stopPropagation(); setWorkflowOpenFor(b); }}
                                            title="Workflow & history"
                                        />
                                    </td>
                                    <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                                    <td className="table-list-cell">{b.code || "—"}</td>
                                    <td className="table-list-cell"><span className="table-list-cell-title cell-name-lock">{b.workflow_state === 'approved' && (<Lock className="lock-icon lock-icon--sm" aria-label="Locked — approved" />)}{userName(b.staff_user_id)}</span></td>
                                    <td className="table-list-cell">{typeName(b.leave_type_id)}</td>
                                    <td className="table-list-cell">{b.year}</td>
                                    <td className="table-list-cell">{b.allotted}</td>
                                    <td className="table-list-cell">{b.used}</td>
                                    <td className="table-list-cell">{b.pending}</td>
                                    <td className="table-list-cell">
                                        <span className={`status-badge xs ${b.is_active === 0 ? "danger" : "success"}`}>{b.is_active === 0 ? "Inactive" : "Active"}</span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                    <Pagination
                        page={page}
                        pageSize={pageSize}
                        totalRecords={balances.length}
                        onPageChange={setPage}
                        onPageSizeChange={setPageSize}
                        pageSizeOptions={[10, 25, 50, 100]}
                        recordLabel="balances"
                    />
                </div>
                </div>
            </div>

            <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} size="sm" data-testid="MOD_balance">
                <Modal.Header>{headerText}</Modal.Header>
                <Modal.Body>
                    {isApproved && (
                        <div className="approved-banner">
                            <Lock size={14} /> Record is approved and locked. Reopen via the workflow button to edit.
                        </div>
                    )}
                    <div className="flex flex-col gap-y-3">
                        <FormControl.SearchSelect
                            label="Staff"
                            required
                            value={staffUserId || null}
                            onChange={(v) => setStaffUserId(v ? Number(v) : 0)}
                            placeholder="Select staff..."
                            options={users.map((u) => ({ value: u.id, label: `${u.name}${u.email ? ` (${u.email})` : ""}` }))}
                            disabled={!!editing}
                            data-testid="SEL_balance_staff"
                        />
                        <FormControl.SearchSelect
                            label="Leave Type"
                            required
                            value={leaveTypeId || null}
                            onChange={(v) => setLeaveTypeId(v ? Number(v) : 0)}
                            placeholder="Select leave type..."
                            options={types.map((t) => ({ value: t.id, label: `${t.name} (${t.code})` }))}
                            disabled={!!editing}
                            data-testid="SEL_balance_type"
                        />
                        <FormControl.Input label="Year" type="number" value={String(year)} onChange={(e) => setYear(Number(e.target.value))} disabled={!!editing} data-testid="INP_balance_year" />
                        <FormControl.Input label="Allotted" type="number" value={String(allotted)} onChange={(e) => setAllotted(Number(e.target.value))} disabled={fieldsDisabled} data-testid="INP_balance_allotted" />
                    </div>
                </Modal.Body>
                {(showCreateBtn || (editing && canModify)) && (
                    <Modal.Footer className={editing ? "app-modal-footer--split" : ""}>
                        {/* Bottom-left: Active/Inactive lifecycle toggle (existing record), but not if approved */}
                        {editing && canModify && !isApproved && (
                            <Button
                                variant={isActive ? "inactive-outline" : "active-outline"}
                                icon={isActive ? CircleMinus : CircleCheck}
                                onClick={() => handleToggleActive(!isActive)}
                                disabled={togglingActive || saving}
                                data-testid="TGL_balance_active"
                            >
                                {togglingActive ? "..." : isActive ? "Inactive" : "Active"}
                            </Button>
                        )}
                        {showCreateBtn && (
                            <Button variant="fill" icon={Save} onClick={handleSave} disabled={isInvalid || saving} data-testid="BTN_balance_save">
                                Submit
                            </Button>
                        )}
                        {showEditActions && (
                            isFieldEditMode ? (
                                <Button variant="update" icon={Check} onClick={handleSave} disabled={saving} data-testid="BTN_balance_save">
                                    Update
                                </Button>
                            ) : (
                                <Button variant="fill" icon={Edit2} onClick={() => setIsFieldEditMode(true)} data-testid="BTN_balance_edit_unlock">Edit</Button>
                            )
                        )}
                    </Modal.Footer>
                )}
            </Modal>

            <WorkflowModal
                isOpen={!!workflowOpenFor}
                onClose={closeWf}
                kind="hr_leave_balance"
                id={workflowOpenFor ? Number(workflowOpenFor.id) : null}
                onChanged={fetchAll}
            />

            <Toast toast={toast} onClose={() => setToast(null)} />
        </div>
    );
};

export default LeaveBalancesAdmin;
